import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import AutoreleaseContextMessage from "./AutoreleaseContextMessage";
import AutoreleaseDisclaimer from "./AutoreleaseDisclaimer";
import AcceptQuestionnaireSplitButton from "./AcceptQuestionnaireSplitButton";
import AutoreleaseConfirmModal from "./AutoreleaseConfirmModal";
import CancelRequestConfirmModal from "./CancelRequestConfirmModal";
import { formatVqqName } from "../../../utils/vqqNameFormat";

// The bordered panel that sits in the VQQ Details header (next to the Export menu)
// when Autorelease is eligible. Holds the contextual message, the Accept Questionnaire
// split button (Autorelease / Accept+Update / Cancel), and the eligibility disclaimer.
const AutoreleasePanel = ({
  eligibility,
  selectedPast,
  onViewLastSubmitted,
  onAcceptAndUpdate,
  onAutoreleaseConfirmed,
  onCancelRequestConfirmed,
  rfisDetails,
  isAutoreleasePending = false,
  isCancelPending = false,
}) => {
  const [autoreleaseModalOpen, setAutoreleaseModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const multiSegment = (eligibility?.past_submissions?.length ?? 0) >= 2;
  const autoreleaseDisabled = multiSegment && !selectedPast;
  const autoreleaseDisabledReason = autoreleaseDisabled
    ? "Select a previously submitted segment first"
    : undefined;

  const handleAutoreleaseClick = () => {
    if (autoreleaseDisabled) return;
    setAutoreleaseModalOpen(true);
  };

  // Keep modals open during mutation so the user sees the spinner; close once parent
  // confirms the mutation resolved (success or error).
  const closeAutoreleaseModal = () => {
    if (!isAutoreleasePending) setAutoreleaseModalOpen(false);
  };
  const closeCancelModal = () => {
    if (!isCancelPending) setCancelModalOpen(false);
  };

  return (
    <>
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5 space-y-3 max-w-2xl">
        <div className="space-y-4">
          <AutoreleaseContextMessage
            templateName={eligibility?.template_name}
            selectedPast={selectedPast}
            onViewLastSubmitted={onViewLastSubmitted}
            returnToId={rfisDetails?.questionnaire_id}
          />
          <div className="flex justify-end">
            <AcceptQuestionnaireSplitButton
              autoreleaseDisabled={autoreleaseDisabled}
              autoreleaseDisabledReason={autoreleaseDisabledReason}
              selectedPast={selectedPast}
              onAutorelease={handleAutoreleaseClick}
              onAcceptAndUpdate={onAcceptAndUpdate}
              onCancelRequest={() => setCancelModalOpen(true)}
            />
          </div>
        </div>
        <AutoreleaseDisclaimer />
      </div>

      <AutoreleaseConfirmModal
        open={autoreleaseModalOpen}
        onClose={closeAutoreleaseModal}
        eligibility={eligibility}
        selectedPast={selectedPast}
        rfisDetails={rfisDetails}
        loading={isAutoreleasePending}
        onConfirm={() => {
          onAutoreleaseConfirmed(selectedPast, () =>
            setAutoreleaseModalOpen(false)
          );
        }}
      />

      <CancelRequestConfirmModal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        templateName={eligibility?.template_name || formatVqqName(rfisDetails?.name)}
        loading={isCancelPending}
        onConfirm={() => {
          onCancelRequestConfirmed(() => setCancelModalOpen(false));
        }}
      />
    </>
  );
};

// Placeholder shown while the eligibility check is in-flight. Matches the real panel's
// outer dimensions so the layout doesn't jump when eligibility resolves.
export const AutoreleasePanelSkeleton = () => (
  <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5 space-y-3 max-w-2xl">
    <div className="space-y-3">
      <Skeleton height={14} count={2} />
      <div className="flex justify-end">
        <Skeleton width={220} height={44} borderRadius={6} />
      </div>
    </div>
    <Skeleton height={12} width="60%" />
  </div>
);

export default AutoreleasePanel;
